import { Jurisdiction } from '@enview/interface/types/Jurisdiction';
import { Organization } from '@enview/interface/types/Organization';
import PolicyDataType from '@enview/interface/types/PolicyDataType';
import { Team } from '@enview/interface/types/Team';
import { createSelector } from '@reduxjs/toolkit';
import assign from 'lodash-es/assign';
import omit from 'lodash-es/omit';
import union from 'lodash-es/union';
import uniqBy from 'lodash-es/uniqBy';
import { TeamAPI } from '../../api';
import { Action, State, Thunk } from '../@types';
import { LOGOUT } from '../AuthenticationDux/types';
import { SET_ORG_ID, SET_TEAM_ID, TeamModeState } from './types';

// REDUCER
export default function reducer(state: TeamModeState, action: Action): TeamModeState {
  switch (action.type) {
    case SET_TEAM_ID:
      return assign({}, state, {
        teamId: action.teamId,
        userInitiatedLogOut: false,
      });
    case SET_ORG_ID:
      return assign({}, state, {
        orgId: action.orgId,
        userInitiatedLogOut: false,
      });
    case LOGOUT:
      return assign({}, omit(state, ['teamId', 'team', 'orgId']), {
        teamId: -1,
        userInitiatedLogOut: true,
      });
    default:
      return state || {};
  }
}

// ACTION CREATORS
const setTeamId = (teamId: number): Action => {
  return {
    type: SET_TEAM_ID,
    teamId,
  };
};

export const setOrgId = (orgId?: number): Action => {
  return {
    type: SET_ORG_ID,
    orgId,
  };
};

// SELECTORS
export const getTeamMode = (state: State): number => {
  return state.teamMode.teamId;
};

export const getTeamModeTeam = (state: State): Team | undefined => {
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.select(undefined)(state);
  return teamMemberships?.find((tm) => tm.teamId === state.teamMode.teamId)?.team;
};

export const getOrgIdForTeamMode = (state: State): number | undefined => {
  return state.teamMode.orgId;
};

export const getOrgForTeamMode = (state: State): Organization | undefined => {
  return state.account.organizationUser?.organizations.find(
    (org) => org.id === state.teamMode.orgId,
  );
};

const getOrganizationUserOrganizations = (state: State) =>
  state.account.organizationUser?.organizations;

const getSupportedJurisdictionsState = (state: State) =>
  state.account.supportedJurisdictions;

const getFilterByPolicyDataType = (
  _state: State,
  filterByPolicyDataType?: PolicyDataType,
) => filterByPolicyDataType;

// Memoized so that in personal workspace mode (no org selected), this returns a
// stable array reference when its inputs haven't changed. Recomputing a new array
// on every call caused components consuming this selector to re-render on every
// store update while in personal workspace mode.

const memoizedGetJurisdictionsForTeamMode = createSelector(
  [
    getOrgForTeamMode,
    getOrganizationUserOrganizations,
    getSupportedJurisdictionsState,
    getFilterByPolicyDataType,
  ],
  (
    teamModeOrg,
    organizations,
    supportedJurisdictions,
    filterByPolicyDataType,
  ): Jurisdiction[] => {
    if (teamModeOrg) return teamModeOrg.jurisdictions;
    // TODO: Move this to a real list of all-US jurisdictions in the frontend, also figure out why we need to have this in 2 places
    if (!organizations) return supportedJurisdictions || [];
    const orgJurisdictions = organizations.reduce((jurArray: Jurisdiction[], org) => {
      return union(
        jurArray,
        org.jurisdictions.filter((j) => {
          if (filterByPolicyDataType == PolicyDataType.Regulation)
            return j.hasRegulationAccess;
          else if (filterByPolicyDataType == PolicyDataType.Bill)
            return j.hasBillAccess;
          else return true;
        }),
      );
    }, []);
    return uniqBy(orgJurisdictions, 'name');
  },
);

export const getJurisdictionsForTeamMode = memoizedGetJurisdictionsForTeamMode as (
  state: State,
  filterByPolicyDataType?: PolicyDataType,
) => Jurisdiction[];

// THUNKS
export const setTeamModeData = (): Thunk => {
  return (dispatch, getState) => {
    const { data: memberships } = TeamAPI.endpoints.getUsersTeamMemberships.select(
      undefined,
    )(getState());
    if (memberships) {
      const teamModeId = getState().teamMode.teamId;
      const currentTeam = memberships.find((m) => m.teamId === teamModeId)?.team;
      if (currentTeam) {
        dispatch(setOrgId(currentTeam?.organizationId));
      }
    }
  };
};

export const setTeamMode = (teamId: number): Thunk => {
  return (dispatch) => {
    dispatch(setTeamId(teamId));
    dispatch(setTeamModeData());
  };
};
